import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { Col, Hidden, Row, Visible } from 'react-grid-system';
import ImageUploading from 'react-images-uploading';
import * as Yup from 'yup';
import { useReturnContext } from '../../../contexts/returnContext';
import { useTranslation } from '../../../contexts/translationContext';
import {
  constructNumberArray,
  getValueLabelFromNumber,
  tImage,
  tOrderLineForm,
  tOrderLineFormData,
} from '../../../contexts/zod';
import useReturnForms, { ReturnFormsAnswer } from '../../../hooks/useReturnForms';
import { Ioption, useReturnReasons } from '../../../hooks/useReturnReasons';
import useStatusMessage from '../../../hooks/useStatusMessage';
import Resize from '../../../utils/base64-resizer';
import { theme } from '../../../utils/theme';
import Spacing from '../../atoms/Spacing';
import Typography from '../../atoms/Typography';
import IconButton from '../../molecules/IconButton';
import LoadingSpinner from '../../molecules/LoadingSpinner';
import PrimaryButton from '../../molecules/PrimaryButton';
import SecondaryButton from '../../molecules/SecondaryButton';
import TextArea from '../../molecules/TextArea';
import FormikDropdown from './FormikDropdown';
import ReturnFormComponents from './ReturnFormComponents';

export type tFileUploadErrors = {
  duplicateFileError: boolean;
};

function topFunction() {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
}

const OrderLineForm: FC<tOrderLineForm> = ({ setOpen, layout, ...props }) => {
  const returnData = props.returnData || undefined;

  const { productNumber, quantity } = props;
  const { reasons, loading } = useReturnReasons(productNumber);
  const {
    addToReturns,
    updateToReturns,
    uploadImage,
    deleteImage,
  } = useReturnContext();
  const { translate } = useTranslation();
  const { numberOfItems } = useStatusMessage(props);
  const [uploadingImages, setUploadingImages] = useState<boolean>(false);
  const [imagesToDelete, setImagesToDelete] = useState<tImage[]>([]);
  const [fileUploadErrors, setFileUploadErrors] = useState<tFileUploadErrors>({
    duplicateFileError: false,
  });
  const [selectedReason, setSelectedReason] = useState<Ioption>({} as Ioption);

  const maxFileSize = 1e7; // 10MB
  const maxNumberOfImages = 3;

  const { sortedReturnForms, answers, isAnswersValid } = useReturnForms(props.salesOrderLineId, returnData?.reason);

  const validUploadTypes = [
    'jpg', 'JPG',
    'jpeg', 'JPEG',
    'png', 'PNG',
    'gif', 'GIF',
    'bmp', 'BMP',
    'tiff', 'TIFF',
    'webp', 'WEBP',
    'avif', 'AVIF',
    'heic', 'HEIC',
    'pdf', 'PDF',
  ];

  const cannotPreviewTypes = [
    'image/heic',
    'application/pdf',
  ];

  const initialValues: tOrderLineFormData = React.useMemo(
    () => ({
      amount: returnData?.amount || undefined,
      reason: returnData?.reason || '',
      returnDescription: returnData?.returnDescription || '',
      images: returnData?.images || [],
      returnForms: sortedReturnForms.reduce((returnFormValues, returnForm) => {
        const values = returnForm.components.reduce((componentValues, component) => {
          const getDefaultValue = () => {
            switch (component.componentConfiguration.type) {
              case 'Select':
                return '';
              case 'Text':
                return '';
              default:
                return null;
            }
          };

          const value = {
            type: component.componentConfiguration.type,
            value: answers[component.id] || getDefaultValue(),
          };

          return { ...componentValues, [component.id]: value };
        }, {});

        return { ...returnFormValues, [returnForm.id]: values };
      }, {}),
    }),
    [returnData],
  );

  useEffect(() => {
    if (returnData?.reason) {
      setSelectedReason(
        reasons.find((item) => item.value === returnData.reason),
      );
    }
  }, [reasons, returnData?.reason]);

  // eslint-disable-next-line consistent-return
  const getMaxValue = () => {
    if (returnData && quantity - numberOfItems === 0) {
      return returnData.amount;
    }
    if (returnData && quantity - numberOfItems !== 0) {
      return returnData.amount + quantity - numberOfItems;
    }
  };

  const min = 1;
  const max = getMaxValue();

  const x = Array.from({ length: max - min + 1 }, (_, i) => i + min);

  const returnableQuantity =
    returnData && returnData.reason
      ? getValueLabelFromNumber(x)
      : getValueLabelFromNumber(constructNumberArray(quantity - numberOfItems));

  const validationSchema = Yup.object({
    amount: Yup.number().required(
      `${translate('quantity', null)} ${translate(
        'is_required',
        null,
      ).toLowerCase()}`,
    ),
    reason: Yup.string().required(
      `${translate('return_reason', null)} ${translate(
        'is_required',
        null,
      ).toLowerCase()}`,
    ),
    returnDescription: Yup.string(),
  });

  const returnFormsSchema = Yup.object({
    returnForms: sortedReturnForms.reduce((formSchemas, returnForm) => {
      const values = returnForm.components.reduce((componentSchemas, component) => {
        const getSchema = () => {
          switch (component.componentConfiguration.type) {
            case 'Select':
              return Yup.string();
            case 'Toggle':
              return Yup.boolean();
            case 'DatePicker':
              return Yup.mixed();
            default:
              return Yup.string();
          }
        };

        let schema = getSchema();

        if (component.required) {
          schema = schema.required(translate('is_required', null));
        }

        return componentSchemas.concat(Yup.object({ [component.id]: Yup.object({ value: schema }) }));
      }, Yup.object({}));

      return formSchemas.concat((Yup.object({ [returnForm.id]: values })));
    }, Yup.object({})),
  });

  const selectLayoutOption = () => {
    if (layout === 'orderLine') {
      return (
        <PrimaryButton
          disabled={uploadingImages}
          loading={uploadingImages}
          type="submit"
          icon="package-plus"
          translateKey="add_return"
        />
      );
    }
    return (
      <Row gutterWidth={24}>
        <Hidden xs sm>
          <Col />
        </Hidden>
        <Col xs={12} sm="content">
          <Visible xs>
            <Spacing spacing={1} />
          </Visible>
          <SecondaryButton
            type="button"
            onClick={() => setOpen(false)}
            translateKey="cancel"
          />
        </Col>

        <Col xs={12} sm="content">
          <Visible xs>
            <Spacing spacing={1} />
          </Visible>
          <PrimaryButton
            disabled={uploadingImages}
            loading={uploadingImages}
            type="submit"
            icon="package-plus"
            translateKey="update_return"
          />
        </Col>
      </Row>
    );
  };

  const getShortFileName = (fileName?: string) => {
    if (!fileName) return '';
    const fileNameSplit = fileName?.split('.');
    const fileNameExtension = fileNameSplit[fileNameSplit.length - 1];
    const fileNameWithoutExtension = fileNameSplit.slice(0, fileNameSplit.length - 1).join('.');

    return fileNameWithoutExtension.length > 6
      ? `${fileNameWithoutExtension.substring(0, 6)}...${fileNameExtension}`
      : `${fileNameWithoutExtension}.${fileNameExtension}`;
  };

  const getReadableReturnReason = (values: tOrderLineFormData) =>
    reasons.find((item) => item.value === values.reason).label;

  const getIsClaim = (values: tOrderLineFormData) =>
    reasons.find((item) => item.value === values.reason)?.isClaim ?? false;

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema.concat(returnFormsSchema),
    onSubmit: async (values: tOrderLineFormData) => {
      const invalidImageUpload = selectedReason?.canUploadPhoto &&
        selectedReason?.requiresPhoto &&
        values.images?.length === 0;

      if (invalidImageUpload || !isAnswersValid()) {
        return;
      }

      if (!selectedReason?.canUploadPhoto) {
        formik.setFieldValue('images', []);
      }

      if (imagesToDelete.length > 0) {
        imagesToDelete.forEach(async (image) => {
          await deleteImage(image.reference);
          imagesToDelete.splice(imagesToDelete.indexOf(image), 1);
        });
      }

      setUploadingImages(true);
      formik.setFieldValue(
        'images',
        await values.images?.reduce(async (imagesP, image) => {
          Promise.resolve();
          const images = await imagesP;

          if (!image.reference) {
            if (
              image.file?.type === 'image/jpeg'
              || image.file?.type === 'image/jpg'
              || image.file?.type === 'image/png'
            ) {
              // eslint-disable-next-line no-param-reassign
              image.dataURL = await Resize(image.dataURL, 2);
            }
            const { reference } = await uploadImage(image);

            if (image.dataURL) {
              values.images?.push({
                ...image,
                salesOrderLineId: props.salesOrderLineId,
                dataURL: image.dataURL as string,
                file: image.file,
                reference: reference as string,
                reason: values.reason,
              });
            }
          }

          return images;
        }, Promise.resolve([] as tImage[])),
      );

      // eslint-disable-next-line no-param-reassign
      values.images = values.images?.filter(
        (img) => img.reference !== undefined,
      );
      setUploadingImages(false);

      // eslint-disable-next-line no-param-reassign
      values.returnForms = Object.entries(values.returnForms).flatMap(([id, form]) => Object.entries(form).map(([returnFormComponentId, answer]) => ({
        answer: answer as ReturnFormsAnswer['answer'],
        returnFormComponentId: Number(returnFormComponentId),
        salesOrderLineId: props.salesOrderLineId,
        reason: values.reason,
      })));

      if (layout === 'returnLine') {
        updateToReturns({
          ...props,
          returnData: {
            ...values,
            readableReason: getReadableReturnReason(values),
          },
          initialReturnReason: props.returnData.reason,
        });
        setOpen(false);
      } else {
        topFunction();
        addToReturns({
          ...props,
          returnData: {
            ...values,
            isClaim: getIsClaim(values),
            readableReason: getReadableReturnReason(values),
          },
        });
        setOpen(false);
      }
    },
  });

  if (loading) {
    return (
      <Row align="center" justify="center">
        <LoadingSpinner color={theme.colors.grey[80]} />
      </Row>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Row>
        <Col xs={12} lg={8}>
          <Row gutterWidth={32}>
            <Col xs={12} md={4} lg={4}>
              <FormikDropdown
                name="amount"
                error={formik.touched.amount && formik.errors.amount}
                translateKey="select_quantity"
                options={returnableQuantity}
                onChange={(data) =>
                  formik.setFieldValue('amount', parseInt(data.value, 10))
                }
                value={formik.values.amount?.toString()}
                className={undefined}
              />
            </Col>
            <Col xs={12} md={8} lg={8}>
              <FormikDropdown
                name="reason"
                error={formik.touched.reason && formik.errors.reason}
                translateKey="placeholder_dropdown_reasons"
                options={
                  !props.mayBeReturned && props.mayBeClaimed
                    ? reasons.filter((r) => r.isClaim)
                    : reasons
                }
                onChange={(data) => {
                  formik.setFieldValue('reason', data.value);
                  setSelectedReason(data);
                }}
                value={formik.values.reason}
                className={undefined}
              />
            </Col>
            <Col>
              <Spacing spacing={3} />
              <TextArea
                value={formik.values.returnDescription}
                label="add_comment"
                name="returnDescription"
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} lg={4}>
          { selectedReason?.canUploadPhoto && (
          <ImageUploading
            multiple
            maxFileSize={maxFileSize}
            acceptType={validUploadTypes}
            allowNonImageType
            maxNumber={maxNumberOfImages}
            value={formik.values.images}
            onChange={(data) => {
              setFileUploadErrors({ duplicateFileError: false });

              const images = data.reduce((uniqueImages, image) => {
                const isDuplicate = uniqueImages.find(
                  (uniqueImage) =>
                    uniqueImage.dataURL === image.dataURL &&
                    uniqueImage.salesOrderLineId === props.salesOrderLineId,
                );

                if (isDuplicate) {
                  setFileUploadErrors({ duplicateFileError: true });
                }

                if (!isDuplicate && image.dataURL) {
                  uniqueImages.push({
                    ...image,
                    salesOrderLineId: props.salesOrderLineId,
                    dataURL: image.dataURL as string,
                    file: image.file,
                    reference: image.reference as string | undefined,
                  });
                }

                  return uniqueImages;
                }, [] as tImage[]);
                formik.setFieldValue('images', images);
              }}
          >
              {({
                imageList,
                onImageUpload,
                onImageRemove,
                dragProps,
                errors,
              }) => (
                <div>
                  <div className="upload__image-wrapper">
                    <div style={{ display: 'flex', marginTop: '18px' }}>
                      <div style={{ marginRight: '10px' }}>
                        <IconButton
                          icon="add-photo"
                          iconColor="#ffffff"
                          backgroundColor="#000000"
                          onClick={onImageUpload}
                          {...dragProps}
                        />
                      </div>
                      <div>
                        <Typography translateKey="upload_images_title" variant="h5" />
                        <Typography translateKey="upload_images_subtitle" variant="body3" />
                        {errors?.maxFileSize && <Typography translateParams={{ filesize: maxFileSize / 1e+6 }} translateKey="upload_images_filesize_error" color="red" variant="body3" />}
                        {errors?.acceptType && <Typography translateKey="upload_images_filetype_error" color="red" variant="body3" />}
                        {errors?.maxNumber && <Typography translateParams={{ count: maxNumberOfImages }} translateKey="upload_images_count_error" color="red" variant="body3" />}
                        {fileUploadErrors?.duplicateFileError && <Typography translateKey="upload_images_duplicate_error" color="red" variant="body3" />}
                        {selectedReason?.requiresPhoto && formik.submitCount > 0 && imageList.length <= 0 && <Typography translateKey="upload_images_required_error" color="red" variant="body3" />}
                      </div>
                    </div>
                  </div>
                  <Spacing spacing={3} />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'start',
                    }}
                  >
                    {imageList.map((image, index) => (
                      <div style={{ position: 'relative', marginRight: 10 }} key={image.file?.name} className="image-item">
                        {!cannotPreviewTypes.find((type) => type === image.file?.type) &&
                          <img src={image.dataURL} title={image.file?.name} alt="" style={{ width: '80px', maxHeight: '100px' }} />
                        }
                        {image.file?.type === 'image/heic' && (<img src="heic_image.png" title={image.file?.name} alt="" style={{ width: '80px', maxHeight: '100px' }} />)}
                        {image.file?.type === 'application/pdf' && <img src="pdf_image.png" title={image.file?.name} alt="" style={{ width: '80px', maxHeight: '100px' }} />}
                        <span style={{ fontSize: '1.2rem', position: 'absolute', bottom: -15, left: 0, width: '80px' }}>
                          {getShortFileName(image.file?.name)}
                        </span>
                        <div style={{ top: 0, left: 0, position: 'absolute' }}>
                          <IconButton
                            icon="minus"
                            iconColor={theme.colors.grey[100]}
                            backgroundColor={theme.colors.grey[40]}
                            onClick={() => {
                              const imageToDelete = formik.values.images?.find(
                                (img) =>
                                  img.dataURL === imageList[index].dataURL,
                              );
                              if (imageToDelete?.reference) {
                                setImagesToDelete([
                                  ...imagesToDelete,
                                  imageToDelete,
                                ]);
                              }
                              onImageRemove(index);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
            )}
          </ImageUploading>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Spacing spacing={3} />
          {sortedReturnForms.map((returnForm) => (
            <Row
              key={returnForm.id}
              align="center"
              justify="between"
              style={{ marginBottom: '24px' }}
            >
              <Col xs={12}>
                <Typography variant="h3" style={{ marginBottom: '12px' }}>
                  {returnForm.headerTranslationKey}
                </Typography>
                <Typography variant="p" style={{ marginBottom: '24px' }}>
                  {returnForm.descriptionTranslationKey}
                </Typography>
              </Col>
              <ReturnFormComponents
                components={returnForm.components}
                savedAnswers={answers}
                errors={formik.errors.returnForms ? formik.errors.returnForms[returnForm.id] : undefined}
                onSubmit={(newAnswers) => {
                  formik.setFieldValue('returnForms', {
                    ...formik.values.returnForms,
                    [returnForm.id]: {
                      ...formik.values.returnForms[returnForm.id],
                      ...newAnswers,
                    },
                  });
                }}
              />
            </Row>
          ))}
        </Col>
      </Row>
      <Row>
        <Col>
          <Spacing spacing={3} />
          {selectLayoutOption()}
        </Col>
      </Row>
    </form>
  );
};

export default OrderLineForm;
